import {
  multiFactor,
  TotpMultiFactorGenerator,
  type TotpSecret,
  type User
} from 'firebase/auth';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toLatinDigits } from '@finvox/shared/digits';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';

type Props = {
  user: User;
  onEnrolled?: () => void;
};

export function MfaEnrollment({ user, onEnrolled }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [enrolled, setEnrolled] = useState(() => multiFactor(user).enrolledFactors.length > 0);

  const readEnrolled = useCallback(() => multiFactor(user).enrolledFactors.length > 0, [user]);

  const refreshEnrolled = useCallback(async () => {
    try {
      await user.reload();
    } catch {
      /* reload can fail when extensions block Firebase; use cached MFA factors */
    }
    const active = readEnrolled();
    setEnrolled(active);
    if (active) onEnrolled?.();
  }, [user, onEnrolled, readEnrolled]);

  async function startEnroll() {
    setBusy(true);
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(secret);
      setQrUrl(secret.generateQrCodeUrl(user.email ?? 'user', 'FinVox'));
    } catch (e) {
      toastError(toast, e, 'mfa-enroll');
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnroll() {
    if (!totpSecret) return;
    setBusy(true);
    try {
      const cred = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code);
      await multiFactor(user).enroll(cred, 'Authenticator');
      setTotpSecret(null);
      setQrUrl(null);
      setCode('');
      toast.success(t('settings.mfaEnrolled'), 'mfa-enroll');
      await refreshEnrolled();
    } catch (e) {
      toastError(toast, e, 'mfa-enroll');
    } finally {
      setBusy(false);
    }
  }

  const qrImage =
    qrUrl &&
    `https://quickchart.io/qr?text=${encodeURIComponent(qrUrl)}&size=200&margin=1`;

  return (
    <div className="mt-3 space-y-3 text-sm">
      <p>{enrolled ? t('settings.mfaActive') : t('settings.mfaHint')}</p>
      {!enrolled && (
        <>
          <Button variant="secondary" onClick={() => void startEnroll()} disabled={busy}>
            {t('settings.mfaStart')}
          </Button>
          {qrImage && (
            <img
              src={qrImage}
              alt={t('settings.mfaQrAlt')}
              className="mx-auto rounded border border-[var(--fv-border)] bg-white p-2"
              width={200}
              height={200}
            />
          )}
          {qrUrl && (
            <p className="break-all text-xs text-[var(--fv-text-muted)]">{qrUrl}</p>
          )}
          {totpSecret && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="max-w-[8rem]"
                value={code}
                onChange={(e) =>
                  setCode(toLatinDigits(e.target.value).replace(/\D/g, '').slice(0, 6))
                }
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              <Button onClick={() => void confirmEnroll()} disabled={busy || code.length < 6}>
                {t('settings.mfaConfirm')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
